import React from "react";
import {
  Controller,
  FieldPath,
  FieldValues,
  RegisterOptions,
  UseFormReturn,
} from "react-hook-form";
import { SingleSelector, SingleSelectProps } from "../selector/SingleSelector";
import { FormFieldControl } from "./FormFieldControl";

export type SingleSelectorFieldProps<
  TFormValues extends FieldValues,
  TValue extends string,
> = Omit<
  SingleSelectProps<TValue>,
  | "value"
  | "onChange"
  | "name"
  | "form"
  | "isInvalid"
  | "isRequired"
  | "isDisabled"
> & {
  form: UseFormReturn<TFormValues>;
  name: FieldPath<TFormValues>;
  rules?: RegisterOptions<TFormValues, FieldPath<TFormValues>>;
  label?: string;
  helperText?: React.ReactNode;
  isRequired?: boolean;
  isDisabled?: boolean;
};

export function SingleSelectorField<
  TFormValues extends FieldValues,
  TValue extends string,
>({
  form,
  name,
  rules,
  label,
  helperText,
  placeholder,
  isRequired,
  isDisabled,
  options,
  enableDeselect,
  ...selectorProps
}: SingleSelectorFieldProps<TFormValues, TValue>) {
  const requiredRule =
    rules?.required ??
    (isRequired ? `${label ?? "This field"} is required` : false);

  return (
    <FormFieldControl
      form={form}
      name={name}
      label={label}
      helperText={helperText}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      <Controller
        control={form.control}
        name={name}
        rules={{
          ...rules,
          required: requiredRule,
        }}
        render={({ field, fieldState }) => (
          <SingleSelector
            {...selectorProps}
            options={options}
            enableDeselect={enableDeselect}
            // Single Selector uses undefined to represent an empty selection,
            // but RHF prefers to use an empty string.
            value={field.value || undefined}
            onChange={(value: TValue | undefined) =>
              field.onChange(value ?? "")
            }
            isRequired={isRequired}
            isDisabled={isDisabled || form.formState.isSubmitting}
            isInvalid={!!fieldState.error && !form.formState.isSubmitting}
            placeholder={placeholder ?? label}
          />
        )}
      />
    </FormFieldControl>
  );
}
